import {
  AMCReportTypeSelect,
  useGetReportTypesQuery,
  useValidateCustomQueryMutation,
} from "~/store/mystore/amc.redux";
import {
  Box,
  CircularProgress,
  Grid,
  Paper,
  Typography,
  useTheme,
} from "@material-ui/core";
import React, { Suspense, lazy, useEffect, useState } from "react";

import AutoSizedButton from "~/components/buttons/autoSizedButton";
import { CheckCircleOutlined } from "@material-ui/icons";
import CopyButton from "~/components/buttons/copyButton";
import GenericSvgIconWrapper from "~/icons/genericSvgIconWrapper";
import LoadingIndicator from "~/components/loadingIndicator/loadingIndicator";
import SaveAMLReportDialog from "./saveAMLReportDialog";
import ValidationError from "./validationResult";
import { ValidationResult } from "~/pages/singleChannel/amc/customAmcReport";
import styled from "styled-components";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

// Lazy load CodeMirror component
const CodeMirrorEditor = lazy(() => import("./codeMirrorEditor"));

const EditorContainer = styled(Paper)`
  width: 100%;
  box-sizing: border-box;
  padding: 0.5rem 0.75rem;
  ${({ theme }) => `
    border-radius: ${theme.shape.borderRadius}px;
    border: 1px solid ${theme.palette.border.main};
  `};
`;

const EditorContent = styled(Box)`
  position: relative;
  width: 100%;
  height: 24rem;
`;

// TO_DO: remove this default error message
const DEFAULT_ERROR_MESSAGE =
  "We encountered an internal error. Please try again later. If the problem persists, please contact support.";

interface CustomReportCodeEditorProps {
  mid: string;
  initialCode: string;
  validationResult: ValidationResult;
  setValidationResult: (result: ValidationResult) => void;
}
interface LocationState {
  reportTypeId?: string;
  onlyCodeDisplay?: boolean;
}

const CustomReportCodeEditor: React.FC<CustomReportCodeEditorProps> = ({
  mid,
  initialCode,
  validationResult,
  setValidationResult,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const location = useLocation<LocationState>();
  const editReportId = location.state?.reportTypeId;
  const onlyCodeDisplay = location.state?.onlyCodeDisplay;

  const [isCodeEdited, setIsCodeEdited] = useState(false);

  const { reportTypes } = useGetReportTypesQuery(
    {
      filterType: AMCReportTypeSelect.ALL,
      activeOnly: true,
    },
    {
      selectFromResult: ({ data }) => {
        return { reportTypes: data?.reportTypes };
      },
    }
  );

  const reportTypeToEdit = reportTypes?.find(
    (reportType) => reportType._id === editReportId
  );

  const [code, setCode] = useState(reportTypeToEdit?.query ?? initialCode);

  const [dialogOpen, setDialogOpen] = useState<boolean>(false);

  const handleCodeChange = (value: string) => {
    setCode(value);
    setIsCodeEdited(true);
    setValidationResult({
      status: null,
      errorMessage: null,
    });
  };

  // This useEffect is triggered when the user clicks on AML query tile
  // on the chat section, when the query is updated we should reset the
  // validation result and the code editor
  useEffect(() => {
    setCode(initialCode);
  }, [initialCode]);

  // This useEffect is triggered when the user clicks on edit or copy code button
  // in the report library page
  useEffect(() => {
    if (reportTypeToEdit) {
      setCode(reportTypeToEdit.query);
    }
  }, [reportTypeToEdit]);

  const [validateCustomQuery, { isLoading: isValidateCustomQueryLoading }] =
    useValidateCustomQueryMutation();

  const handleValidateCustomQuery = async (successCallback?: () => void) => {
    try {
      if (validationResult.status === "success") {
        successCallback && successCallback();
      } else {
        const response = await validateCustomQuery({ mid, query: code });
        if ("error" in response) {
          setValidationResult({
            status: "error",
            errorMessage: DEFAULT_ERROR_MESSAGE,
          });
        } else if ("data" in response) {
          setValidationResult({
            status: response.data.status,
            errorMessage: response.data.errorMessage ?? null,
          });
          if (response.data.status === "success") {
            successCallback && successCallback();
          }
        }
      }
    } catch (error) {
      setValidationResult({
        status: "error",
        errorMessage: DEFAULT_ERROR_MESSAGE,
      });
    }
  };

  const handleValidateSuccessCallback = async () => {
    if (validationResult.status === "error") {
      setDialogOpen(false);
    } else {
      setDialogOpen(true);
    }
  };

  return (
    <>
      <EditorContainer elevation={0}>
        {/* Top Bar */}
        <Box pb={1}>
          <Grid container alignItems="center" spacing={1}>
            <Grid item xs={12} sm={6}>
              <Typography variant="h2">
                {t("amc.customReports.codeEditorCode")}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Grid
                container
                alignItems="center"
                justifyContent="flex-end"
                spacing={1}
              >
                <Grid item>
                  <CopyButton textToCopy={code} />
                </Grid>
                <Grid item>
                  <AutoSizedButton
                    variant="outlined"
                    startIcon={
                      <GenericSvgIconWrapper
                        $color={theme.palette.text.secondary}
                      >
                        {isValidateCustomQueryLoading ? (
                          <CircularProgress size={18} color="inherit" />
                        ) : (
                          <CheckCircleOutlined />
                        )}
                      </GenericSvgIconWrapper>
                    }
                    onClick={() => handleValidateCustomQuery()}
                    disabled={isValidateCustomQueryLoading}
                  >
                    <Typography color="textSecondary">
                      {t("amc.customReports.codeEditorValidate")}
                    </Typography>
                  </AutoSizedButton>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Box>

        <EditorContent>
          <Suspense fallback={<LoadingIndicator />}>
            <CodeMirrorEditor value={code} onChange={handleCodeChange} />
          </Suspense>
        </EditorContent>

        {/* Bottom Bar */}
        <Box pt={1}>
          <Grid
            container
            alignItems="center"
            justifyContent="flex-end"
            spacing={1}
          >
            <Grid item>
              <AutoSizedButton
                variant="contained"
                color="secondary"
                startIcon={
                  <GenericSvgIconWrapper
                    $color={theme.palette.secondary.contrastText}
                  >
                    <CheckCircleOutlined />
                  </GenericSvgIconWrapper>
                }
                onClick={() =>
                  handleValidateCustomQuery(handleValidateSuccessCallback)
                }
                disabled={isValidateCustomQueryLoading}
              >
                {t("amc.customReports.codeEditorSave")}
              </AutoSizedButton>
            </Grid>
            {validationResult.status !== null && (
              <Grid item xs={12}>
                <ValidationError
                  status={validationResult.status}
                  title={
                    validationResult.status === "success"
                      ? t("amc.customReports.validationSuccess")
                      : t("amc.customReports.validationFailed")
                  }
                  errorMessage={
                    validationResult.status === "success" ||
                    validationResult.errorMessage === null
                      ? null
                      : validationResult.errorMessage.replace(
                          "The workflow was determined to be invalid:",
                          ""
                        )
                  }
                />
              </Grid>
            )}
          </Grid>
        </Box>
      </EditorContainer>
      <SaveAMLReportDialog
        open={validationResult.status === "success" && dialogOpen}
        onClose={() => {
          setDialogOpen(false);
          setValidationResult({
            status: null,
            errorMessage: null,
          });
        }}
        mid={mid}
        query={code}
        isCodeEdited={isCodeEdited}
        reportTypeToEdit={onlyCodeDisplay ? undefined : reportTypeToEdit}
      />
    </>
  );
};

export default CustomReportCodeEditor;
